// The Everything split test's homepage card (docs/dip-everything-split-test.md §2e): the homepage
// range's first card for an arm-B visitor, sending them to the routine page, where Everything is
// pre-selected. Markup: the Everything block in sections/dip-home-offers.liquid (the range's own
// .dip-offers__card scaffold).
//
// Priced by the SAME code as the PDP and routine choice cards (assets/dip-evr.js quoteAll over the
// routine chooser's data island, snippets/dip-routine-chooser.liquid part 'island'), so the
// homepage can never quote a different first order or saving from the page it links to. Nothing
// money-bearing is typed in the Liquid. An arm-A visitor (or the test off) never sees it: the card
// is hidden by CSS before paint ([data-evr-only]) and removed here, so the rail counts three.

import { evrOn } from '@theme/dip-arm';
import { quoteAll, benefitItems, paintBenefits, paintPlaces } from '@theme/dip-evr';
import { hhOn, split as hhSplit, paintBar as hhPaintBar } from '@theme/dip-hh';
import { WPM } from '@theme/dip-plan-engine';

const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
const money = (minorUnits) => _fmt.format((minorUnits || 0) / 100);

class DipEvrPromo extends HTMLElement {
  connectedCallback() {
    if (!evrOn()) { this.remove(); return; }
    let model = null;
    try { model = JSON.parse(this.querySelector('[data-dip-data]').textContent); } catch { model = null; }
    if (!model?.products) { this.remove(); return; }
    const E = model.evr || {};
    const offer = model.offer || { active: false };
    // The usage rate only sets each line's cadence (the price is the pack's). Same rule as the
    // routine chooser (assets/dip-routine.js #rate), at its arm-B defaults (dishwasher E.dwRuns).
    const rateOf = (key) => {
      const p = model.products[key];
      if (!p) return 0;
      let washes = Number(p.defaultWashes || 4);
      if (key === 'dishwasher' && Number(E.dwRuns) > 0) washes = Number(E.dwRuns);
      if (key === 'msc') return (washes / 4) / WPM;
      return washes * (key === 'laundry' ? Number(p.fixedFactor || 1.5) : Number(p.fixedFactor || 1));
    };
    const q = quoteAll({
      products: model.products, offer, hardware: model.hardware || null,
      freeShipMinor: Number(this.dataset.freeShipMinor || 0), money, rateOf,
    });
    if (!q) { this.remove(); return; }
    const on = q.offerSave > 0;
    const card = this.querySelector('[data-evr-card="all"]');
    const price = this.querySelector('[data-evr-first]');
    const lead = this.querySelector('[data-evr-lead]');
    const hh = hhOn();
    // Half now, half later, arm B: "From £29.50 today", no saving chip; the rest and its date sit in the bar.
    if (price) price.textContent = hh && on ? `${money(q.first)} ${offer?.hh?.first || 'today'}` : money(on ? q.first : q.raw);
    // Without the offer (a past purchaser) the line reads "From £x", never "First order from".
    if (lead && (!on || hh)) lead.textContent = E.from ?? 'From';
    const save = this.querySelector('[data-evr-save]');
    if (save) {
      save.textContent = !hh && q.save > 0 ? (E.banner || 'Save [save] today').replace('[save]', money(q.save)) : '';
      save.hidden = hh || !(q.save > 0);
    }
    // Three bullets at most on the range card so it lines up with the product cards (Charlie, 16 Oct, both arms).
    paintBenefits(card, benefitItems(q, { E, offer, money }).filter(([, t]) => t).slice(0, 3), this);
    hhPaintBar(this.querySelector('[data-hh-bar]'), hhSplit([{ raw: q.raw, first: q.first, on }], offer), offer, money);
    paintPlaces(card, q.keys || [], hh ? (offer?.hh?.evrPlaces || E.places) : E.places);
    this.classList.add('is-ready');
  }
}

if (!customElements.get('dip-evr-promo')) customElements.define('dip-evr-promo', DipEvrPromo);
